import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, ChevronRight } from 'lucide-react';

function LinkedInIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.114 20.452H3.558V9h3.556v11.452z" />
    </svg>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-1.5 text-sm text-cogLight/70 hover:text-cogLight transition-colors group"
      >
        <ChevronRight className="w-3.5 h-3.5 text-cogAccentBlue shrink-0 group-hover:translate-x-0.5 transition-transform" />
        <span>{children}</span>
      </Link>
    </li>
  );
}

function SocialButton({ href, label, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-10 h-10 rounded-full bg-cogLight/10 flex items-center justify-center text-cogLight hover:bg-cogAccentBlue hover:text-cogInk transition-colors"
    >
      {children}
    </a>
  );
}

function Footer() {
  return (
    <footer className="relative overflow-hidden bg-cogBrand text-cogLight/70 pt-16 pb-8 px-6 lg:px-12">
      {/* Decorative background glow (built purely from existing palette, no new colors) */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(circle at 15% 20%, rgba(9,124,169,0.35), transparent 40%), radial-gradient(circle at 85% 75%, rgba(0,170,204,0.25), transparent 45%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-cogLight/15">

        {/* About */}
        <div>
          <Link to="/" className="flex items-center mb-4 w-fit">
            <img src="/logo.png" alt="Interconnect Systems Logo" className="h-16 w-auto object-contain brightness-0 invert" />
          </Link>
          <p className="text-sm text-cogLight/70 leading-relaxed">
           Interconnect Systems designs and manufactures high-reliability wiring harnesses for mission-critical applications, delivering trusted solutions for defense, aerospace, EV, medical, and industrial automation.
          </p>
          <div className="flex gap-3 mt-6">
            <SocialButton href="https://www.linkedin.com/in/pvs-harness-solutions-829583402" label="LinkedIn">
              <LinkedInIcon className="w-4 h-4" />
            </SocialButton>
          </div>
        </div>

        {/* Office / Contact */}
        <div>
          <h6 className="text-cogLight text-lg font-semibold mb-4">Office:</h6>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-cogAccentBlue" />
              <span>
                #1, 78/6, Sarkar Chowk, Marunji, Mulshi,
                <br />
                Pune, Maharashtra – 411057
              </span>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 shrink-0 text-cogAccentBlue" />
              <a href="mailto:pvsharness@gmail.com" className="hover:text-cogLight transition-colors">
                pvsharness@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4 shrink-0 text-cogAccentBlue" />
              <a href="tel:+917892945169" className="hover:text-cogLight transition-colors">
                +91-7892945169
              </a>
            </li>
          </ul>
        </div>

        {/* Useful Links */}
        <div>
          <h6 className="text-cogLight text-lg font-semibold mb-4">Useful Links</h6>
          <ul className="space-y-3">
            <FooterLink to="/">Home</FooterLink>
            <FooterLink to="/about">About us</FooterLink>
            <FooterLink to="/services">Services</FooterLink>
            <FooterLink to="/products">Products &amp; Design</FooterLink>
            <FooterLink to="/contact">Contact us</FooterLink>
          </ul>
        </div>

        {/* Our Services */}
        <div>
          <h6 className="text-cogLight text-lg font-semibold mb-4">Our Services</h6>
          <ul className="space-y-3">
            <FooterLink to="/services">Custom Design &amp; Prototyping</FooterLink>
            <FooterLink to="/services">Laser Marking &amp; Overmolding</FooterLink>
            <FooterLink to="/services">EMI Shielding &amp; Braiding</FooterLink>
            <FooterLink to="/services">Full Assembly &amp; Testing</FooterLink>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 text-xs text-cogLight/60 text-center sm:text-left">
        <div className="space-y-1">
          <p>
            © Copyright <span className="font-semibold text-cogLight">PVS HARNESS SOLUTIONS</span>. All Rights Reserved
          </p>
          <p>
            Designed, Developed &amp; Hosted by{' '}
            <Link
              to="https://www.thefastestcoder.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-cogLight hover:underline"
            >
              Fastest Coder Innovations Pvt. Ltd.
            </Link>
          </p>
        </div>

        
      </div>
    </footer>
  );
}

export default Footer;